<template>
  <div>
    <b-row class="mb-1">
      <b-col class="d-flex justify-content-between align-items-center">
        <h1 class="font-weight-bolder mb-0">
          Ticket #{{ ticketData.ticket_number || '-' }}
        </h1>

        <div>
          <b-button
            class="mr-1"
            variant="outline-primary"
            :disabled="loading"
            @click="runAiRefinement"
          >
            Refinar con IA
          </b-button>

          <b-button
            variant="outline-success"
            :disabled="loading"
            @click="createIssue"
          >
            Crear Issue GitLab
          </b-button>
        </div>
      </b-col>
    </b-row>

    <b-overlay :show="loading">
      <b-row>
        <b-col md="8">
          <b-card>
            <b-form @submit.prevent="save">
              <b-form-group label="Título">
                <b-form-input v-model.trim="form.title" />
              </b-form-group>

              <b-form-group label="Tipo">
                <b-form-select
                  v-model="form.type"
                  :options="typeOptions"
                />
              </b-form-group>

              <b-form-group label="Descripción">
                <b-form-textarea
                  v-model.trim="form.description"
                  rows="4"
                />
              </b-form-group>

              <b-form-group label="Resultado esperado">
                <b-form-textarea
                  v-model.trim="form.expected_result"
                  rows="3"
                />
              </b-form-group>

              <b-row>
                <b-col md="4">
                  <b-form-group label="Estado">
                    <b-form-select
                      v-model="form.status"
                      :options="statusOptions"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="Prioridad">
                    <b-form-select
                      v-model="form.priority"
                      :options="priorityOptions"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="Estimación">
                    <b-form-select
                      v-model="form.estimate"
                      :options="estimateOptions"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-form-group label="Notas técnicas">
                <b-form-textarea
                  v-model.trim="form.technical_notes"
                  rows="4"
                />
              </b-form-group>

              <b-form-group label="Anexar imágenes">
                <b-form-file
                  v-model="newImages"
                  multiple
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  placeholder="Selecciona imágenes"
                  drop-placeholder="Suelta aquí las imágenes"
                />
              </b-form-group>

              <div v-if="ticketImages.length > 0" class="mb-1">
                <small class="text-muted d-block mb-50">Imágenes actuales</small>
                <div
                  v-for="image in ticketImages"
                  :key="image.id"
                  class="d-flex justify-content-between align-items-center py-50 border-bottom"
                >
                  <a
                    :href="image.url"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ image.filename }}
                  </a>
                  <b-button
                    size="sm"
                    :variant="removeImageIds.includes(image.id) ? 'danger' : 'outline-danger'"
                    @click="toggleImageRemoval(image.id)"
                  >
                    {{ removeImageIds.includes(image.id) ? 'Se eliminará' : 'Quitar' }}
                  </b-button>
                </div>
              </div>

              <b-button
                type="submit"
                variant="primary"
              >
                Guardar cambios
              </b-button>
              <b-button
                class="ml-1"
                variant="outline-secondary"
                :to="{ name: 'tickets' }"
              >
                Regresar
              </b-button>
            </b-form>
          </b-card>
        </b-col>

        <b-col md="4">
          <b-card class="mb-1">
            <h5>Certificación IA</h5>
            <p class="mb-50">
              <strong>Estatus ticket:</strong> {{ statusLabel(ticketData.status) }}
            </p>
            <p class="mb-50">
              <strong>Solicitante:</strong> {{ requesterLabel(ticketData) }}
            </p>
            <p class="mb-50">
              <strong>Creado:</strong> {{ formatDate(ticketData.created_at) }}
            </p>
            <p class="mb-50">
              <strong>Completado:</strong> {{ formatDate(ticketData.completed_at) }}
            </p>
            <p class="mb-50">
              <strong>Vence estimado:</strong> {{ formatDate(ticketData.estimated_due_at) }}
            </p>
            <p class="mb-50">
              <strong>Control tiempo:</strong>
              <b-badge :variant="timeStatusVariant(ticketData.estimate_time_status)">
                {{ timeStatusLabel(ticketData.estimate_time_status) }}
              </b-badge>
            </p>
            <p class="mb-50">
              <strong>Estatus IA:</strong> {{ ticketData.ai_certification_status || 'pending' }}
            </p>
            <p class="mb-50">
              <strong>Importancia:</strong> {{ ticketData.importance || 'n/a' }}
            </p>
            <p class="mb-50">
              <strong>Impacto:</strong> {{ ticketData.impact || 'n/a' }}
            </p>
            <p class="mb-50">
              <strong>Criticidad:</strong> {{ ticketData.criticality || 'n/a' }}
            </p>
            <p class="mb-50">
              <strong>Estimación:</strong> {{ estimateLabel(ticketData.estimate) }}
            </p>
            <p class="mb-50">
              <strong>Días estimados:</strong> {{ formatDays(ticketData.estimated_days) }}
            </p>
            <p class="mb-50">
              <strong>Días transcurridos:</strong> {{ formatDays(ticketData.estimated_elapsed_days) }}
            </p>
            <p class="mb-50">
              <strong>Días restantes:</strong> {{ formatDays(ticketData.estimated_days_remaining) }}
            </p>
            <p class="mb-0">
              <strong>GitLab:</strong>
              <a
                v-if="ticketData.gitlab_issue_url"
                :href="ticketData.gitlab_issue_url"
                target="_blank"
              >
                Abrir issue
              </a>
              <span v-else>Sin issue</span>
            </p>
          </b-card>

          <b-card>
            <h6>Resumen IA</h6>
            <p class="mb-1">
              {{ ticketData.ai_summary || 'Sin resumen aún.' }}
            </p>

            <h6>Criterios</h6>
            <pre class="small mb-1">{{ ticketData.ai_acceptance_criteria || 'Sin criterios aún.' }}</pre>

            <h6>Preguntas faltantes</h6>
            <pre class="small mb-1">{{ ticketData.ai_questions || 'Sin preguntas.' }}</pre>

            <h6>Solución técnica sugerida</h6>
            <pre class="small mb-1">{{ ticketData.ai_technical_solution || 'Sin solución técnica aún.' }}</pre>

            <h6>Plan de implementación sugerido</h6>
            <pre class="small mb-0">{{ ticketData.ai_implementation_plan || 'Sin plan aún.' }}</pre>
          </b-card>
        </b-col>
      </b-row>
    </b-overlay>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  data() {
    return {
      form: {
        title: '',
        type: 'new_feature',
        description: '',
        expected_result: '',
        priority: 'medium',
        status: 'backlog',
        estimate: null,
        technical_notes: '',
      },
      newImages: [],
      removeImageIds: [],
      typeOptions: [
        { value: 'bug', text: 'Bug' },
        { value: 'improvement', text: 'Mejora' },
        { value: 'new_feature', text: 'Nueva funcionalidad' },
        { value: 'ui_change', text: 'Cambio UI' },
        { value: 'performance', text: 'Performance' },
        { value: 'other', text: 'Otro' },
      ],
      statusValues: [
        'backlog',
        'pending_specification',
        'ready_for_development',
        'in_development',
        'in_review',
        'done',
        'rejected',
      ],
      priorityOptions: [
        { value: 'low', text: 'Baja' },
        { value: 'medium', text: 'Media' },
        { value: 'high', text: 'Alta' },
        { value: 'urgent', text: 'Urgente' },
      ],
      estimateOptions: [
        { value: null, text: 'Sin estimación' },
        { value: 'l', text: 'L (1 día)' },
        { value: 'xl', text: 'XL (2-3 días)' },
        { value: 'xxl', text: 'XXL (4-5 días)' },
        { value: 'xxxl', text: 'XXXL (1-2 semanas)' },
        { value: 'epic', text: 'EPIC (2+ semanas)' },
      ],
    }
  },
  computed: {
    ...mapGetters('tickets', ['ticket', 'loading']),
    ticketData() {
      return this.ticket || {}
    },
    statusOptions() {
      return this.statusValues.map(value => ({ value, text: this.statusLabel(value) }))
    },
    ticketImages() {
      return Array.isArray(this.ticketData.images) ? this.ticketData.images : []
    },
  },
  created() {
    this.load()
  },
  methods: {
    ...mapActions('tickets', [
      'fetchTicket',
      'updateTicket',
      'refineTicketWithAi',
      'createGitlabIssue',
    ]),
    normalizeImagesInput(value) {
      if (!value) return []
      if (Array.isArray(value)) return value
      if (typeof value.length === 'number') return Array.from(value)
      return []
    },
    statusLabel(value) {
      if (!value) return '-'
      const key = `ticketStatus.${value}`
      const translated = this.$t(key)
      return translated === key ? value : translated
    },
    timeStatusLabel(value) {
      if (!value) return '-'
      const key = `ticketTimeStatus.${value}`
      const translated = this.$t(key)
      return translated === key ? value : translated
    },
    timeStatusVariant(value) {
      if (value === 'overdue' || value === 'completed_overdue') return 'danger'
      if (value === 'completed_on_time') return 'success'
      if (value === 'on_time') return 'primary'
      return 'secondary'
    },
    requesterLabel(ticket) {
      const name = ticket?.requester_name
      const email = ticket?.requester_email
      if (name && email) return `${name} (${email})`
      return name || email || '-'
    },
    estimateLabel(value) {
      if (!value) return 'Sin estimación'

      const labels = {
        l: 'L (1 día)',
        xl: 'XL (2-3 días)',
        xxl: 'XXL (4-5 días)',
        xxxl: 'XXXL (1-2 semanas)',
        epic: 'EPIC (2+ semanas)',
      }

      return labels[value] || value
    },
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      return date.toLocaleDateString()
    },
    formatDays(value) {
      if (value === null || value === undefined || value === '') return 'n/a'
      const number = Number(value)
      if (Number.isNaN(number)) return 'n/a'

      const abs = Math.abs(number)
      const suffix = abs === 1 ? 'día' : 'días'
      return `${number} ${suffix}`
    },
    mapTicketToForm(ticket) {
      this.form = {
        title: ticket.title || '',
        type: ticket.ticket_type || 'new_feature',
        description: ticket.description || '',
        expected_result: ticket.expected_result || '',
        priority: ticket.priority || 'medium',
        status: ticket.status || 'backlog',
        estimate: ticket.estimate || null,
        technical_notes: ticket.technical_notes || '',
      }
    },
    toggleImageRemoval(id) {
      if (this.removeImageIds.includes(id)) {
        this.removeImageIds = this.removeImageIds.filter(curr => curr !== id)
      } else {
        this.removeImageIds.push(id)
      }
    },
    async load() {
      try {
        const payload = await this.fetchTicket(this.$route.params.ticketId)
        const ticket = payload?.data || payload
        this.mapTicketToForm(ticket || {})
        this.removeImageIds = []
        this.newImages = []
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error?.response?.data?.messages?.[0] || 'No se pudo cargar el ticket',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
    async save() {
      try {
        const response = await this.updateTicket({
          id: this.$route.params.ticketId,
          ticket: {
            ...this.form,
            images: this.normalizeImagesInput(this.newImages),
            remove_image_ids: this.removeImageIds,
          },
        })

        const refreshed = response?.data || response
        this.mapTicketToForm(refreshed || this.ticketData)
        this.removeImageIds = []
        this.newImages = []

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Ticket actualizado',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error?.response?.data?.messages?.[0] || 'No se pudo actualizar el ticket',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
    async runAiRefinement() {
      try {
        const response = await this.refineTicketWithAi(this.$route.params.ticketId)
        const refreshed = response?.data || this.ticketData
        this.mapTicketToForm(refreshed)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Ticket refinado por IA',
            icon: 'CpuIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error?.response?.data?.messages?.[0] || 'No se pudo ejecutar IA',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
    async createIssue() {
      try {
        await this.createGitlabIssue(this.$route.params.ticketId)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Issue creado en GitLab',
            icon: 'GitMergeIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error?.response?.data?.messages?.[0] || 'No se pudo crear issue',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
  },
}
</script>

<style scoped>
pre {
  white-space: pre-wrap;
}
</style>
